import React, { useState } from 'react';
import { Pin, Edit2, Trash2, CheckCircle, AlertCircle, Lightbulb, Flag, MessageSquare } from 'lucide-react';
import { Card, Button } from '../ui';
import { WorkNoteDto, NoteType } from '../../services/workNotes';
import './NoteCard.css';

interface NoteCardProps {
  note: WorkNoteDto;
  onEdit?: (noteId: string) => void;
  onDelete?: (noteId: string) => void;
  onTogglePin?: (noteId: string) => void;
  isEditing?: boolean;
  showStage?: boolean;
}

const noteTypeIcons: Record<NoteType, React.ReactNode> = {
  General: <MessageSquare size={14} />,
  StatusChange: <CheckCircle size={14} />,
  Milestone: <Flag size={14} />,
  Issue: <AlertCircle size={14} />,
  Idea: <Lightbulb size={14} />,
};

const noteTypeLabels: Record<NoteType, string> = {
  General: 'Note',
  StatusChange: 'Status Change',
  Milestone: 'Milestone',
  Issue: 'Issue',
  Idea: 'Idea',
};

// Extract stage transition from note content (e.g., "✅ Idea → Composition" or "⬅️ Composition → Idea")
const extractStageTransition = (content: string): string | null => {
  // Match patterns like "✅ Stage1 → Stage2" or "⬅️ Stage1 → Stage2"
  const match = content.match(/^[✅⬅️]\s*(.+?)\s*→\s*(.+?)(?:\n|$)/);
  if (match) {
    return `${match[1]} → ${match[2]}`;
  }
  return null;
};

// Get the display label for a note
const getNoteLabel = (note: WorkNoteDto): string => {
  // For stage transition notes, extract the transition from content
  if (note.noteType === 'StatusChange' || note.noteType === 'Issue') {
    const transition = extractStageTransition(note.content);
    if (transition) {
      return transition;
    }
  }

  // For general notes, show the stage they were written in
  if (note.noteType === 'General' && note.stageAtCreation) {
    return note.stageAtCreation;
  }

  // Fallback to type-based labels for other types
  return noteTypeLabels[note.noteType];
};

// Get the content to display (strip the transition prefix if present)
const getDisplayContent = (note: WorkNoteDto): string => {
  if (note.noteType === 'StatusChange' || note.noteType === 'Issue') {
    // Remove the prefix line (✅ Stage → Stage or ⬅️ Stage → Stage)
    const lines = note.content.split('\n');
    if (lines[0].match(/^[✅⬅️]/)) {
      // Skip the first line and any empty lines after it
      return lines.slice(1).join('\n').trim();
    }
  }
  return note.content;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

export const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onEdit,
  onDelete,
  onTogglePin,
  isEditing = false,
  showStage = false,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    if (showDeleteConfirm) {
      onDelete?.(note.id);
      setShowDeleteConfirm(false);
    } else {
      setShowDeleteConfirm(true);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <Card className={`note-card ${note.isPinned ? 'note-card--pinned' : ''} note-card--type-${note.noteType.toLowerCase()}`}>
      <div className="note-card__header">
        <div className="note-card__type">
          {noteTypeIcons[note.noteType]}
          <span>{getNoteLabel(note)}</span>
        </div>
        <div className="note-card__meta">
          {showStage && (
            <span className="note-card__stage">{note.stageAtCreation}</span>
          )}
          <span className="note-card__date">{formatDate(note.createdAt)}</span>
          {note.updatedAt && note.updatedAt !== note.createdAt && (
            <span className="note-card__edited">(edited)</span>
          )}
        </div>
      </div>

      <div className="note-card__content">
        <p>{getDisplayContent(note)}</p>
      </div>

      <div className="note-card__footer">
        {note.authorName && (
          <span className="note-card__author">{note.authorName}</span>
        )}
        <div className="note-card__actions">
          {onTogglePin && (
            <Button
              variant="ghost"
              size="sm"
              className={`note-card__action ${note.isPinned ? 'note-card__action--active' : ''}`}
              onClick={() => onTogglePin(note.id)}
              title={note.isPinned ? 'Unpin note' : 'Pin note'}
            >
              <Pin size={14} />
            </Button>
          )}
          {onEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="note-card__action"
              onClick={() => onEdit(note.id)}
              title="Edit note"
              disabled={isEditing}
            >
              <Edit2 size={14} />
            </Button>
          )}
          {onDelete && (
            <>
              {showDeleteConfirm ? (
                <div className="note-card__delete-confirm">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDelete}
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelDelete}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="note-card__action note-card__action--danger"
                  onClick={handleDelete}
                  title="Delete note"
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default NoteCard;
